import { t } from 'i18next';

import { useEnhancedMutation } from '@/modules/common/application';
import {
  Company,
  UpdateUserInput,
  UpdateUserSettingMutation,
  UpdateUserSettingMutationVariables,
  User,
  UserAccountStatus,
} from '@/modules/common/types';
import { UPDATE_USER_SETTING } from '@/services/graphql/mutations';
import { GET_USER } from '@/services/graphql/queries';

import { useMessage } from './context/message';

const useUserSettings = (
  onCompleted?: () => void,
  skipSuccessMessage?: boolean,
  skipErrorMessage?: boolean
) => {
  const { setErrorMessage, setSuccessMessage } = useMessage();

  const [updateUserMutation, { loading }] = useEnhancedMutation<
    UpdateUserSettingMutation,
    UpdateUserSettingMutationVariables
  >(UPDATE_USER_SETTING, {
    onCompleted: () => {
      if (!skipSuccessMessage) {
        setSuccessMessage(t('messages.updateUser.success'));
      }
      if (onCompleted) {
        onCompleted();
      }
    },
    onError: () => {
      if (!skipErrorMessage) {
        setErrorMessage(t('messages.updateUser.error'));
      }
    },
    update(cache, result, { variables }) {
      if (!result.data?.updateUser || !variables?.input) return;

      const { user, company } = cache.readQuery<{
        user: User;
        company: Company;
      }>({
        query: GET_USER,
      }) || { user: undefined, company: undefined };

      if (!user) return;

      const valuesToOverride = Object.fromEntries(
        Object.entries(variables.input).filter(([_, value]) => value !== undefined)
      );

      // Skip cache update if only language is being updated
      const isLanguageOnlyUpdate =
        Object.keys(valuesToOverride).length === 1 && 'language' in valuesToOverride;

      if (!isLanguageOnlyUpdate) {
        cache.writeQuery({
          query: GET_USER,
          data: {
            user: {
              ...user,
              ...valuesToOverride,
              status: UserAccountStatus.USER_INFO_COMPLETED,
            },
            company,
          },
        });
      }
    },
  });

  const updateUser = async (data: UpdateUserInput) => {
    await updateUserMutation({
      variables: {
        input: {
          firstName: data.firstName,
          lastName: data.lastName,
          department: data.department || undefined,
          language: data.language || undefined,
          numberFormat: data.numberFormat || undefined,
          assistantContext: data.assistantContext || undefined,
        },
      },
    });
  };

  return {
    updateUser,
    loading,
  };
};

export default useUserSettings;
